import { useEffect } from "react";
import { useGetActiveRaffleQuery, useGetAccountQuery, type RequestDetail } from "../../store/api";
import { saveActiveRaffleAction } from "../../store/slices/raffle";
import { NoActiveRaffle } from "../home/NoActiveRaffle";
import { useAppDispatch } from "../../store/hooks/useAppDispatch";
import { RootState } from "../../store/types/rootState";
import { useAppSelector } from "../../store/hooks/useAppSelector";
import { Loading } from "../layout/loading";
import FAQDropdown from "../home/FAQDropdown";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import WelcomeBox from "../home/WelcomeBox";
import { RaffleCatalog } from "../home/RaffleCatalog";
import { RaffleFooter, RaffleTemplate } from "../home/RaffleTemplate";

type ErrorWithDetail = { requestDetail?: RequestDetail; status?: number; data?: unknown; error?: unknown };
const ErrorRequestDetail = ({ error }: { error: ErrorWithDetail | null | undefined }) => {
  const d = error?.requestDetail;
  if (!d) return null;
  return (
    <>
      <br />
      <strong>Detalle de la petición:</strong>
      <br />
      URL: {d.fullUrl} · Método: {d.method}
      <br />
      baseUrl: {d.baseUrl} · hostname: {d.hostname}
      <br />
      timestamp: {d.timestamp} · errorStatus: {String(d.errorStatus)} · errorMessage: {d.errorMessage}
    </>
  );
};

export const Home = () => {
  const dispatch = useAppDispatch();

  const accountId = useAppSelector(
    ({ account }: RootState) => account.id,
  );

  const logo = useAppSelector(
    ({ account }: RootState) => account.logo,
  );

  const isMultiRaffle = useAppSelector(
    ({ account }: RootState) => !!account.isMultiRaffle,
  );

  const { data: activeRaffleFounded, isLoading: isLoadingGetActiveRaffle, error: errorGetActiveFaffle, isUninitialized } = useGetActiveRaffleQuery(accountId, {
    skip: accountId === "default" || accountId === "" || isMultiRaffle,
  });

  const {
    data: accountData,
    error: errorGetAccount,
    isLoading: isLoadingAccount,
    isError: isErrorGetAccount,
    isUninitialized: isUninitializedAccount,
  } = useGetAccountQuery(null, {});

  // Debug: por qué a veces se muestra WelcomeBox
  const showWelcomeBox = !(!isLoadingGetActiveRaffle && !isUninitialized) && accountId === "default";
  const showMainContent = !isLoadingGetActiveRaffle && !isUninitialized;
  const hostname = globalThis.location?.hostname ?? "unknown";

  // Reiniciar cada 5 s cuando estamos en WelcomeBox hasta que estemos bien
  useEffect(() => {
    if (!showWelcomeBox) return;
    const id = setInterval(() => {
      globalThis.location?.reload();
    }, 5000);
    return () => clearInterval(id);
  }, [showWelcomeBox]);

  if (isMultiRaffle && accountId !== "default" && accountId !== "") {
    return (
      <>
        <RaffleCatalog />
        <RaffleFooter />
      </>
    );
  }

  if (showWelcomeBox) {
    const debugInfo = {
      hostname,
      accountId,
      getAccount: {
        data: accountData,
        error: errorGetAccount ? (typeof errorGetAccount === "object" && "data" in errorGetAccount ? (errorGetAccount as { data?: unknown }).data : errorGetAccount) : null,
        isLoading: isLoadingAccount,
        isError: isErrorGetAccount,
        isUninitialized: isUninitializedAccount,
      },
      isLoadingGetActiveRaffle,
      isUninitialized,
      error: errorGetActiveFaffle ? String(errorGetActiveFaffle) : null,
      hasActiveRaffle: !!activeRaffleFounded,
      reason: accountId === "default" || accountId === ""
        ? "Query está en skip (accountId es default o vacío)"
        : !showMainContent
          ? "Aún cargando o query no inicializado"
          : "Otro",
    };
    console.log("[Home] Mostrando WelcomeBox — debug:", debugInfo);
  }

  if (!isLoadingGetActiveRaffle && !errorGetActiveFaffle && !isLoadingGetActiveRaffle && activeRaffleFounded) {
      dispatch(saveActiveRaffleAction(activeRaffleFounded));
  }

  return !isLoadingGetActiveRaffle && !isUninitialized ? (
    <>
      {activeRaffleFounded ? <RaffleTemplate raffle={activeRaffleFounded} /> : <NoActiveRaffle logo={logo} />}
      <RaffleFooter />
    </>
  ) : accountId != 'default' ? <Box
          sx={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            padding: 3,
          }}
        >
          <Typography variant="h4" fontWeight="bold" mb={2}>
            Buscando rifas disponibles
          </Typography>
          <CircularProgress />
          {/* <Alert severity="info" sx={{ mt: 2, maxWidth: 600, textAlign: "left" }}>
            <strong>useGetAccountQuery:</strong> isLoading={String(isLoadingAccount)} · isError={String(isErrorGetAccount)}
            <br />
            data: {accountData ? JSON.stringify(accountData) : "null"}
            <br />
            error: {errorGetAccount ? (typeof errorGetAccount === "object" && "status" in errorGetAccount ? `status ${(errorGetAccount as { status?: number }).status} — ${JSON.stringify((errorGetAccount as { data?: unknown }).data ?? (errorGetAccount as object))}` : String(errorGetAccount)) : "ninguno"}
            <ErrorRequestDetail error={errorGetAccount as ErrorWithDetail} />
          </Alert> */}
  </Box> : <>
  <WelcomeBox />
  {/* <Alert severity="info" sx={{ mx: 2, mt: 2 }}>
    <strong>Debug — por qué se muestra WelcomeBox:</strong>
    <br />
    hostname: &quot;{hostname}&quot;
    <br />
    accountId: &quot;{accountId}&quot; (si es &quot;default&quot; o vacío, el query no se ejecuta)
    <br />
    isLoading: {String(isLoadingGetActiveRaffle)} · isUninitialized: {String(isUninitialized)}
    <br />
    error getActiveRaffle: {errorGetActiveFaffle ? (typeof errorGetActiveFaffle === "object" ? JSON.stringify(errorGetActiveFaffle, null, 0) : String(errorGetActiveFaffle)) : "ninguno"}
    <ErrorRequestDetail error={errorGetActiveFaffle as ErrorWithDetail} />
    <br />
    Rifa activa: {activeRaffleFounded ? "sí" : "no"}
    <br />
    <strong>useGetAccountQuery:</strong> isLoading={String(isLoadingAccount)} · isError={String(isErrorGetAccount)} · isUninitialized={String(isUninitializedAccount)}
    <br />
    getAccount data: {accountData ? JSON.stringify(accountData) : "null"}
    <br />
    getAccount error: {errorGetAccount ? (typeof errorGetAccount === "object" && "status" in errorGetAccount ? `${(errorGetAccount as { status?: number }).status} — ${JSON.stringify((errorGetAccount as { data?: unknown }).data ?? errorGetAccount)}` : String(errorGetAccount)) : "ninguno"}
    <ErrorRequestDetail error={errorGetAccount as ErrorWithDetail} />
    <br />
    <em>Revisa también la consola (log &quot;[Home] Mostrando WelcomeBox&quot;)</em>
    <br />
    <strong>La página se reiniciará automáticamente cada 5 segundos.</strong>
  </Alert> */}
  </>;
};
